import type { GameData } from "@/app/games/(details)/types";
import {
  type CampaignQuestState,
  mapCampaignQuestType,
} from "@/components/QuestsV2/types";
import { useGameDetailsContext } from "@/context/GameDetailsContext";
import {
  PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY,
  type PendingSocialQuest,
  linkedAccountPlatforms,
  validateQuest,
} from "@/utils/linkedaccounts/linkedAccounts";
import { useCallback, useEffect, useState } from "react";
import { useAccessToken } from "../useAccessToken";
import { useGetLinkedAccounts } from "../useGetLinkedAccounts";

export type OnboardingQuestState = {
  quest: CampaignQuestState;
  state: "pending" | "connected" | "success" | "error";
  error?: string;
};

export const useGameOnboarding = () => {
  const { completeQuest } = useGameDetailsContext();
  const getAccessToken = useAccessToken();
  const [questState, setQuestState] = useState<OnboardingQuestState | null>(
    null,
  );
  const [focusCount, setFocusCount] = useState(0);
  const { data: linkedAccounts, mutate: refreshLinkedAccounts } =
    useGetLinkedAccounts();

  useEffect(() => {
    const handleFocusChange = () => {
      setFocusCount((prev) => prev + 1);
    };

    document.addEventListener("focusin", handleFocusChange);
    return () => {
      document.removeEventListener("focusin", handleFocusChange);
    };
  }, []);

  useEffect(() => {
    // Use focus count as trigger instead of handling directly in the above useEffect
    if (questState?.state === "pending" && focusCount > 0) {
      refreshLinkedAccounts();
      const socialError = localStorage.getItem("social_quest_error");
      const authError = localStorage.getItem("social_auth_error");
      const linkedAccountPlatform =
        questState.quest.type === "steam_wishlist" ? "steam" : "x";
      const loggedIn =
        (linkedAccountPlatforms
          .get(mapCampaignQuestType(questState.quest.type))
          ?.isLoggedIn() ??
          false) &&
        (linkedAccounts?.linked_accounts.some(
          (account) => account.platform === linkedAccountPlatform,
        ) ??
          false);
      if (
        loggedIn &&
        localStorage.getItem(questState.quest.externalId ?? "") === "true"
      ) {
        // X will trigger this, update UI to show success
        setQuestState({ quest: questState.quest, state: "success" });
      } else if (socialError || authError) {
        setQuestState({
          quest: questState.quest,
          state: "error",
          error: socialError ?? authError ?? undefined,
        });
      } else if (loggedIn) {
        // Steam will trigger this, once connected trigger wishlist flow
        setQuestState({ quest: questState.quest, state: "connected" });
      }
    }
  }, [questState, focusCount, linkedAccounts, refreshLinkedAccounts]);

  const validateSocialQuest = async (gameData: GameData) => {
    if (!questState) return;
    const token = await getAccessToken();
    if (!token) {
      setQuestState({
        quest: questState.quest,
        state: "error",
        error: "Unable to retrieve access token.",
      });
      return false;
    }
    const following = await validateQuest(
      gameData.details.game_id,
      mapCampaignQuestType(questState.quest.type),
      questState.quest.externalId,
      token,
      "",
    );
    if (following) {
      setQuestState({ quest: questState.quest, state: "success" });
      // invalidate quest completions
      completeQuest(questState.quest);
      return true;
    }
    // set state back to connected so they can try again
    setQuestState({
      quest: questState.quest,
      state: "connected",
      error: "Quest validation failed.",
    });
    return false;
  };

  const startSocialQuest = useCallback(
    (gameData: GameData, quest: CampaignQuestState) => {
      // Reset all used states
      localStorage.removeItem("social_auth_error");
      localStorage.removeItem("social_quest_error");
      localStorage.removeItem(quest.externalId);
      setQuestState({ quest, state: "pending" });

      const key = mapCampaignQuestType(quest.type);
      const platform = linkedAccountPlatforms.get(key);
      if (!platform) {
        return;
      }

      const loginOnly = quest.type === "steam_wishlist";
      const url = platform.getAuthorizeUrl(loginOnly);
      const pendingSocialQuest: PendingSocialQuest = {
        gameId: gameData.details.game_id,
        ruleId: quest.externalId,
        questLink: quest.linkUrl || "",
        gameName: gameData.details.name,
        questKey: key,
        loginOnly,
        linkUrl: quest.linkUrl,
      };
      localStorage.setItem(
        PENDING_SOCIAL_QUEST_LOCAL_STORAGE_KEY,
        JSON.stringify(pendingSocialQuest),
      );
      const width = 800;
      const height = 600;
      const left = (screen.width - width) / 2;
      const top = (screen.height - height) / 2;
      window.open(
        url,
        "social-quest",
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`,
      );
    },
    [],
  );
  return {
    startSocialQuest,
    validateSocialQuest,
    questState,
  };
};
